import type {
  DealerDeviceFilterInput,
  DevicesGlobalSearchQuery,
  DevicesGlobalSearchQueryVariables,
} from "@/gql/graphql";
import { DEVICES_GLOBAL_SEARCH_QUERY } from "../global-search-queries";
import type {
  DevicesAutocompleteOptions,
  GlobalSearchApolloClient,
} from "../types";
import {
  GLOBAL_SEARCH_PAGINATION_DEFAULTS,
  executeGlobalSearchQuery,
} from "./executeGlobalSearchQuery";

function buildGlobalSearchDevicesWhere(
  searchTerm: string
): DealerDeviceFilterInput | undefined {
  const trimmedSearchTerm = searchTerm.trim();
  if (!trimmedSearchTerm) {
    return undefined;
  }
  return {
    or: [
      { modelName: { contains: trimmedSearchTerm } },
      { partNumber: { contains: trimmedSearchTerm } },
      { serialNumber: { contains: trimmedSearchTerm } },
      { reference: { contains: trimmedSearchTerm } },
    ],
  };
}

export async function fetchDevices(
  client: GlobalSearchApolloClient,
  accountId: string,
  searchTerm: string,
  signal?: AbortSignal
): Promise<{ totalCount: number; items: DevicesAutocompleteOptions[] }> {
  const where = buildGlobalSearchDevicesWhere(searchTerm);
  if (!where) {
    return { totalCount: 0, items: [] };
  }
  const data = await executeGlobalSearchQuery<
    DevicesGlobalSearchQuery,
    DevicesGlobalSearchQueryVariables
  >({
    client,
    query: DEVICES_GLOBAL_SEARCH_QUERY,
    variables: {
      ...GLOBAL_SEARCH_PAGINATION_DEFAULTS,
      dealerAccountId: accountId,
      initialise: false,
      unassignedOnly: false,
      where,
    },
    signal,
  });
  const response = data?.dealerDevices;
  return {
    totalCount: response?.totalCount ?? 0,
    items: (response?.nodes ?? []).map((d) => ({
      deviceId: d.id,
      deviceName: d.modelName ?? "",
      deviceSerialNumber: d.serialNumber,
      devicePartNumber: d.partNumber,
      deviceReference:
        d.__typename === "DealerDevice" ? (d.reference ?? "") : "",
      iconUrl: d.iconUrl ?? "",
    })),
  };
}
